import React, { useMemo, useState } from "react";

import {
  MainTeaser,
  MapModal,
  StaticMap,
  TeaserProps,
} from "shared/components";
import { Spacer } from "shared/layout";
import { MutedDarkText, TinyText } from "shared/styles";
import { IPlace } from "shared/types";
import { formatAddress, getCountry } from "shared/utils";
import { MapContainer } from "./styles";

interface PlaceTeaserProps extends IPlace, TeaserProps {
  hasMapModal?: boolean;
  hideMap?: boolean;
}

export const PlaceTeaser: React.FC<PlaceTeaserProps> = ({
  hasMapModal,
  hideMap,
  title,
  address: { staticImage, longitude, latitude, ...address },
  ...rest
}) => {
  const [showModal, setShowModal] = useState(false);
  const subTitle = useMemo(() => formatAddress(address).address, [address]);

  const subTitleComponent = useMemo(() => {
    const country = getCountry(address.country);

    return (
      <Spacer size="xxs">
        <Spacer size="xs" spaceDirection="horizontal" align="center">
          <div>{country?.flag}</div>
          <TinyText>{country?.name}</TinyText>
        </Spacer>
        <MutedDarkText as={TinyText}>{address.addressLine1}</MutedDarkText>
        {address.addressLine2 && (
          <MutedDarkText as={TinyText}>{address.addressLine2}</MutedDarkText>
        )}
        <MutedDarkText as={TinyText}>
          {address.zip} {address.city}
        </MutedDarkText>
        {address.state && (
          <MutedDarkText as={TinyText}>{address.state}</MutedDarkText>
        )}
      </Spacer>
    );
  }, [address]);

  return (
    <div>
      {showModal && (
        <MapModal
          handleClose={() => setShowModal(false)}
          popup={{
            title,
            subTitle,
          }}
          coordinates={{ longitude, latitude }}
        />
      )}
      <MainTeaser
        title={title}
        subTitleComponent={subTitleComponent}
        data-testid="place_teaser"
        hasHorizontalContent={!hideMap && !!staticImage}
        onClick={hasMapModal ? () => setShowModal(true) : undefined}
        {...rest}
      >
        {!hideMap && staticImage && (
          <MapContainer>
            <StaticMap
              coordinates={{ longitude, latitude }}
              image={staticImage}
              height="100%"
              width="100%"
              alt={`Static map of ${title}`}
              showOpenButton={hasMapModal}
            />
          </MapContainer>
        )}
      </MainTeaser>
    </div>
  );
};
